import React from 'react';
import { FileCheck, FileX2, FileSymlink, FileQuestionMark, ImagePlus, ImageOff } from 'lucide-react';
import {
  FILE_STATUS_COLUMN_TOOLTIPS,
  FILE_STATUS_GROUP_TOOLTIP,
  getFileStatusStateTooltip,
  type FileStatusValue,
} from './fileStatusLabels';

interface StatusArquivosIconsProps {
  item: any;
  className?: string;
  size?: number;
}

interface StatusIconProps {
  value: FileStatusValue;
  kind: 'save' | 'export' | 'layout';
  size: number;
}

const StatusIcon: React.FC<StatusIconProps> = ({ value, kind, size }) => {
  const tooltip = getFileStatusStateTooltip(kind, value);
  const fullTitle = `${FILE_STATUS_COLUMN_TOOLTIPS[kind]}: ${tooltip}`;

  if (kind === 'save') {
    if (value === null) {
      return (
        <span title={fullTitle} aria-label={fullTitle} role="img">
          <FileQuestionMark className="text-gray-400" width={size} height={size} />
        </span>
      );
    }
    if (value) {
      return (
        <span title={fullTitle} aria-label={fullTitle} role="img">
          <FileCheck className="text-green-400" width={size} height={size} />
        </span>
      );
    }
    return (
      <span title={fullTitle} aria-label={fullTitle} role="img">
        <FileX2 className="text-red-400" width={size} height={size} />
      </span>
    );
  }

  if (kind === 'export') {
    if (value === null) {
      return (
        <span title={fullTitle} aria-label={fullTitle} role="img">
          <FileQuestionMark className="text-gray-400" width={size} height={size} />
        </span>
      );
    }
    if (value) {
      return (
        <span title={fullTitle} aria-label={fullTitle} role="img">
          <FileSymlink className="text-green-400" width={size} height={size} />
        </span>
      );
    }
    return (
      <span title={fullTitle} aria-label={fullTitle} role="img">
        <FileQuestionMark className="text-red-400" width={size} height={size} />
      </span>
    );
  }

  if (value === null) {
    return (
      <span title={fullTitle} aria-label={fullTitle} role="img">
        <FileQuestionMark className="text-gray-400" width={size} height={size} />
      </span>
    );
  }
  if (value) {
    return (
      <span title={fullTitle} aria-label={fullTitle} role="img">
        <ImagePlus className="text-green-400" width={size} height={size} />
      </span>
    );
  }
  return (
    <span title={fullTitle} aria-label={fullTitle} role="img">
      <ImageOff className="text-red-400" width={size} height={size} />
    </span>
  );
};

/**
 * Linha compacta: salvamento · impressão · layout.
 */
export const StatusArquivosIcons: React.FC<StatusArquivosIconsProps> = ({ item, className, size = 20 }) => {
  const arquivo = item?.arquivo ?? {};
  const sysSave: FileStatusValue = typeof arquivo?.sys_save === 'boolean' ? arquivo.sys_save : null;
  const sysExport: FileStatusValue = typeof arquivo?.sys_export === 'boolean' ? arquivo.sys_export : null;
  const sysExportLayout: FileStatusValue =
    typeof arquivo?.sys_export_layout === 'boolean' ? arquivo.sys_export_layout : null;

  return (
    <div
      className={`flex items-center gap-1.5 ${className || ''}`}
      title={FILE_STATUS_GROUP_TOOLTIP}
      aria-label={FILE_STATUS_GROUP_TOOLTIP}
    >
      <StatusIcon kind="save" value={sysSave} size={size} />
      <StatusIcon kind="export" value={sysExport} size={size} />
      <StatusIcon kind="layout" value={sysExportLayout} size={size} />
    </div>
  );
};

const LEGEND_ITEMS: Array<{ kind: keyof typeof FILE_STATUS_COLUMN_TOOLTIPS; shortLabel: string }> = [
  { kind: 'save', shortLabel: 'Salvamento' },
  { kind: 'export', shortLabel: 'Impressão' },
  { kind: 'layout', shortLabel: 'Layout' },
];

export const FileStatusIconsLegend: React.FC<{ className?: string }> = ({ className }) => (
  <div className={`flex flex-wrap items-center gap-x-3 gap-y-1 ${className || ''}`}>
    {LEGEND_ITEMS.map(({ kind, shortLabel }) => (
      <span
        key={kind}
        className="text-[10px] text-gray-500 border-b border-dotted border-gray-600 cursor-help"
        title={FILE_STATUS_COLUMN_TOOLTIPS[kind]}
      >
        {shortLabel}
      </span>
    ))}
  </div>
);
